/**
 * Why each line of a decision table has the figure it has, as CSV columns
 * (bata-ui#166 W4-8, `docs/adr/9120`).
 *
 * The screens explain a line with platform ADR 6038's signed drivers, its binding constraint
 * and its reason code. The CSV carried the two codes and no driver, so the file said WHAT the
 * engine proposed and not WHY. These columns put the why in the file, in two forms:
 *
 *   numbers  one column per driver code this build names, signed, in pairs. They sum to the
 *            line's `current_units`, so a spreadsheet can check the arithmetic the screen
 *            checks (`driverBalance`).
 *   words    the same drivers as one localised sentence, label and code together, and a
 *            localised label beside every code column. The code stays the join key; the
 *            label is for the person reading the file.
 *
 * Absence is kept apart from zero. A line whose platform version published no decomposition
 * leaves every driver `not-reported`. A line that published drivers, but not this one, leaves
 * it `not-ranged`: the driver does not apply to that line. Neither becomes `0`.
 */

import type { ProposalSummary } from '@/api/generated';
import type { ExportCell, ExportColumn, ExportTranslate } from '@/export';

import { toConstraintKey } from './api';
import { driverBalance, toDriverReason } from './explain';
import type { Translate } from './export-provenance';

/**
 * The nine codes platform ADR 6038 publishes, in its order: the six parts of the need, then
 * the engine's change to it (exactly one of case rounding, DC availability and allocation on a
 * line); then ADR 6188's `shipment_cost`, the pairs the transport cost took off after the solve.
 */
export const DRIVER_CODES = [
  'forecast_rate', 'cover_target', 'presentation_minimum', 'on_hand', 'in_transit', 'whole_units',
  'case_rounding', 'dc_availability', 'allocation', 'shipment_cost',
] as const;

/**
 * A code as the catalogue's words, for a workbook column's `display` (DEC-104, `docs/adr/9041`):
 * the CSV keeps the code; a code with no catalogue key, or a key with no words, stays as it is.
 */
export function catalogueWords(key: (code: string) => string | null) {
  return (code: string, translate: ExportTranslate): string => {
    const found = key(code);
    if (found === null) return code;
    const text = translate(found);
    return text === '' || text === found ? code : text;
  };
}

/**
 * Why a driver cell is absent, in the workbook (`absent`, `docs/adr/9041`). `not-ranged` here is
 * "the line published drivers, but not this one" — no contribution — never the assortment state
 * the workbook otherwise words as «Non assortito»; it shows blank, and «Tecnico» says why.
 * `not-reported` (no decomposition) shows «n.d.», with its reason there too. The CSV is unchanged.
 */
const DRIVER_ABSENT: ExportColumn['absent'] = {
  notRanged: 'blank',
  reasonKeys: { 'not-ranged': 'inseason.exportDrivers.absent.notApplicable', 'not-reported': 'inseason.exportDrivers.absent.notPublished' },
};

/** `drivers_unnamed`: none unnamed is a legitimately empty cell; no decomposition is «n.d.». */
const UNNAMED_ABSENT: ExportColumn['absent'] = {
  notRanged: 'blank',
  reasonKeys: { 'not-reported': 'inseason.exportDrivers.absent.notPublished' },
};

/** `drivers_balance` in the workbook: «Sì», «No», or «n.d.» where no decomposition was published. */
const BALANCE_WORDS: Readonly<Record<string, string>> = {
  balanced: 'export.boolean.yes',
  unbalanced: 'export.boolean.no',
  not_published: 'export.cell.notAvailable',
};

/** One numeric column per named driver. Header keys are the drivers' own catalogue keys. */
export const DRIVER_COLUMNS: readonly ExportColumn[] = [
  { key: 'driver_forecast_rate', headerKey: 'inseason.driver.forecast_rate', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_forecast_rate', absent: DRIVER_ABSENT },
  { key: 'driver_cover_target', headerKey: 'inseason.driver.cover_target', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_cover_target', absent: DRIVER_ABSENT },
  { key: 'driver_presentation_minimum', headerKey: 'inseason.driver.presentation_minimum', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_presentation_minimum', absent: DRIVER_ABSENT },
  { key: 'driver_on_hand', headerKey: 'inseason.driver.on_hand', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_on_hand', absent: DRIVER_ABSENT },
  { key: 'driver_in_transit', headerKey: 'inseason.driver.in_transit', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_in_transit', absent: DRIVER_ABSENT },
  { key: 'driver_whole_units', headerKey: 'inseason.driver.whole_units', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_whole_units', absent: DRIVER_ABSENT },
  { key: 'driver_case_rounding', headerKey: 'inseason.driver.case_rounding', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_case_rounding', absent: DRIVER_ABSENT },
  { key: 'driver_dc_availability', headerKey: 'inseason.driver.dc_availability', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_dc_availability', absent: DRIVER_ABSENT },
  { key: 'driver_allocation', headerKey: 'inseason.driver.allocation', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_allocation', absent: DRIVER_ABSENT },
  { key: 'driver_shipment_cost', headerKey: 'inseason.driver.shipment_cost', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.driver_shipment_cost', absent: DRIVER_ABSENT },
  { key: 'drivers_unnamed', headerKey: 'inseason.exportDrivers.column.unnamed', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.drivers_unnamed', absent: UNNAMED_ABSENT },
  {
    key: 'drivers_balance', headerKey: 'inseason.exportDrivers.column.balance', kind: 'label', descriptionKey: 'inseason.columnGuide.proposal.drivers_balance',
    display: catalogueWords((code) => BALANCE_WORDS[code] ?? null),
  },
  {
    key: 'drivers', headerKey: 'inseason.exportDrivers.column.drivers', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.drivers',
    absent: { reasonKeys: { 'not-reported': 'inseason.exportDrivers.absent.notPublished' } },
  },
];

/** The label columns that sit beside a proposal's two codes. */
export const PROPOSAL_LABEL_COLUMNS: readonly ExportColumn[] = [
  { key: 'bound_by_label', headerKey: 'inseason.exportDrivers.column.boundByLabel', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.bound_by_label' },
  { key: 'reason_code_label', headerKey: 'inseason.exportDrivers.column.reasonCodeLabel', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.reason_code_label' },
];

const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };
const NOT_APPLICABLE: ExportCell = { kind: 'missing', state: 'not-ranged' };
const NAMED: ReadonlySet<string> = new Set(DRIVER_CODES);

/** A signed contribution as the sentence shows it: `+3`, `-2`, `0`. */
function signed(value: number): string {
  return value > 0 ? ['+', String(value)].join('') : String(value);
}

/** The driver cells for one proposal, in `DRIVER_COLUMNS` order. */
export function driverCells(proposal: ProposalSummary, translate: Translate): readonly ExportCell[] {
  const drivers = proposal.drivers ?? [];
  if (drivers.length === 0) {
    // No decomposition published (a run written before platform ADR 6038). Not zero drivers.
    return [
      ...DRIVER_CODES.map(() => NOT_REPORTED),
      NOT_REPORTED,
      { kind: 'label', value: 'not_published', code: 'not_published' },
      NOT_REPORTED,
    ];
  }
  const byCode = new Map<string, number>();
  for (const driver of drivers) byCode.set(driver.code, (byCode.get(driver.code) ?? 0) + driver.contribution);
  const unnamed = drivers.filter((driver) => !NAMED.has(driver.code));
  const balance = driverBalance(proposal);
  const balanceCode = balance.kind === 'unbalanced' ? 'unbalanced' : 'balanced';
  const sentence = drivers.map((driver) => {
    const reason = toDriverReason(driver);
    const label = translate(reason.labelKey, reason.labelValues ?? {});
    return [label, `(${driver.code})`, signed(driver.contribution)].join(' ');
  }).join('; ');
  return [
    ...DRIVER_CODES.map((code): ExportCell => {
      const contribution = byCode.get(code);
      return contribution === undefined ? NOT_APPLICABLE : { kind: 'number', raw: contribution };
    }),
    unnamed.length === 0
      ? NOT_APPLICABLE
      : { kind: 'text', value: unnamed.map((driver) => [driver.code, String(driver.contribution)].join('=')).join(';') },
    { kind: 'label', value: balanceCode, code: balanceCode },
    { kind: 'text', value: sentence },
  ];
}

/**
 * «Standard» (client request 2026-10-26): the pairs the store would hold once the line ships —
 * the proposed pairs plus the stock already at the door plus the stock on its way to it, both as
 * POSITIVE pair counts, beside the drivers they are read from.
 *
 * The two stock figures are the line's own `on_hand` and `in_transit` drivers with the sign
 * turned back (platform ADR 6038: `on_hand` = in-transit − position, `in_transit` = −in-transit),
 * so on hand + in transit is exactly the position the run netted off — the same stock the
 * «Requested» and the waterfall were computed from, not a second read of the ledger. A driver the
 * line does not carry is a nought term the platform leaves out ("zero terms are left out"), so
 * it adds nothing; where the run did not measure in-transit separately, its pairs are inside
 * `on_hand`, and the sum is still the whole position. The sign is turned, never dropped: a
 * negative stock (a ledger below zero) stays negative rather than becoming invented pairs.
 *
 * A line with no decomposition at all (a run written before ADR 6038) has no stock figure the
 * file can state: the cell is `not-reported`, with why, never the proposed pairs alone.
 */
export const STANDARD_COLUMN: ExportColumn = {
  key: 'standard', headerKey: 'inseason.exportDrivers.column.standard', kind: 'number', unit: 'pairs',
  descriptionKey: 'inseason.columnGuide.proposal.standard',
  absent: { reasonKeys: { 'not-reported': 'inseason.exportDrivers.absent.standardNotPublished' } },
};

/** Float noise from fractional contributions is not a pair: six decimals, as `driverBalance`'s tolerance. */
const STANDARD_PRECISION = 1e6;

/** A proposal's «Standard» cell: proposed + on hand + in transit, or why it cannot be stated. */
export function standardCell(proposal: Pick<ProposalSummary, 'units' | 'drivers'>): ExportCell {
  const drivers = proposal.drivers ?? [];
  if (drivers.length === 0) return NOT_REPORTED;
  const stock = drivers
    .filter((driver) => driver.code === 'on_hand' || driver.code === 'in_transit')
    .reduce((total, driver) => total - driver.contribution, 0);
  return { kind: 'number', raw: Math.round((proposal.units + stock) * STANDARD_PRECISION) / STANDARD_PRECISION };
}

/** The localised labels of a proposal's binding constraint and reason code. */
export function proposalLabelCells(proposal: ProposalSummary, translate: Translate): readonly ExportCell[] {
  return [
    { kind: 'text', value: translate(toConstraintKey(proposal.bound_by)) },
    { kind: 'text', value: translate(toConstraintKey(proposal.reason_code)) },
  ];
}
